import React, { useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Plus, RotateCcw, Stethoscope, Trash2 } from 'lucide-react';
import type {
  GbuHinweis,
  GefaehrdungEntwurf,
  GefaehrdungsFaktor,
  Kataster,
  MassnahmeArt,
  MassnahmeEntwurf,
  MassnahmeVerweis,
  SifaGbu,
  SifaGbuGefaehrdung,
  SifaGbuMassnahme,
  SifaGbuVorsorge,
  VorsorgeArt,
  VorsorgeEntwurf,
} from '../../../services/sifa/gbuTypes';
import {
  loescheGefaehrdung, loescheMassnahme, loescheVorsorge, speichereGefaehrdung, speichereMassnahme, speichereVorsorge, stelleWiederHer,
} from '../../../services/sifa/gbuService';
import { istAktiv, vorsorgeArtVorschlag } from '../../../services/sifa/gbuLogic';
import { GEFAEHRDUNGSFAKTOREN, faktorLabel } from '../../../services/sifa/kataloge/gefaehrdungsfaktoren';
import { PSA_KATALOG, psaLabel } from '../../../services/sifa/kataloge/psaKatalog';
import { ARBMEDVV_ANLAESSE, anlassLabel } from '../../../services/sifa/kataloge/arbmedvvAnlaesse';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { RueckfrageDialog } from '../vorlagen/RueckfrageDialog';
import { ListenEditor } from './ListenEditor';
import { SpeicherKontext, useSpeicherGruppe } from './useUngespeichert';
import { type ZeilenRegeln, useZeilenEntwurf } from './useZeilenEntwurf';
import { SpeicherStatus } from './SpeicherStatus';
import { RisikoBadge, RisikoWahl } from './RisikoWahl';
import {
  HINWEIS_TON,
  MASSNAHME_ARTEN,
  MASSNAHME_ART_TON,
  VERWEISE,
  VERWEIS_LABEL,
  VORSORGE_ARTEN,
  VORSORGE_ART_LABEL,
  bereinigeGefaehrdung,
  bereinigeMassnahme,
  bereinigeVorsorge,
  bezugsAuswahl,
  entfaelltText,
  formatTag,
  KLAPP_HINWEIS_TEXT,
  gefaehrdungAnker,
  gefaehrdungBegonnen,
  gefaehrdungEntwurfAus,
  gefaehrdungGeaendert,
  gefaehrdungSinnvoll,
  loeschText,
  massnahmeBegonnen,
  massnahmeEntwurfAus,
  massnahmeGeaendert,
  massnahmeSinnvoll,
  mitStabilenSchluesseln,
  offeneZeilenText,
  naechsteSortOrder,
  neueMassnahme,
  neueVorsorge,
  parseZahl,
  pruefeGefaehrdung,
  pruefeMassnahme,
  schalteId,
  vorsorgeAbweichung,
  vorsorgeEntwurfAus,
  vorsorgeGeaendert,
  vorsorgeMitVorschlag,
  vorsorgeNachAnlasswechsel,
  zahlText,
  zuklappenErlaubt,
} from './gbuModulLogic';

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-600';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';
const KNOPF = 'min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold rounded-lg transition-colors disabled:opacity-40';

interface Props {
  gbu: SifaGbu;
  /** null = neue, noch nicht angelegte Gefaehrdung (dann `vorlage`). */
  gefaehrdung: SifaGbuGefaehrdung | null;
  vorlage?: GefaehrdungEntwurf;
  nr: number;
  /** ALLE Massnahmen bzw. Vorsorgeanlaesse dieser Gefaehrdung — aktive und entfallene. */
  massnahmen: SifaGbuMassnahme[];
  vorsorge: SifaGbuVorsorge[];
  kataster: Kataster;
  hinweise: GbuHinweis[];
  bearbeitbar: boolean;
  companyName: string | null;
  /** Aufgeklappt — vom Editor gefuehrt (neue Karte, Sprung aus einem Hinweis). */
  offen: boolean;
  onUmschalten: () => void;
  /** Gespeichert (auch: neu angelegt) — der Editor uebernimmt die Zeile. */
  onGespeichert: (g: SifaGbuGefaehrdung) => void;
  /** Neue Karte verworfen (nie angelegt) bzw. gespeicherte wirklich geloescht. */
  onEntfernt: (id: string | null) => void;
  onMassnahme: (m: SifaGbuMassnahme) => void;
  onMassnahmeEntfernt: (id: string) => void;
  onVorsorge: (v: SifaGbuVorsorge) => void;
  onVorsorgeEntfernt: (id: string) => void;
  onFehler: (text: string) => void;
  /** Frisch laden: nach „entfaellt", nach „Wiederherstellen" und auf Wunsch nach einem Fehler. */
  onNeuLaden: () => void;
}

/**
 * Eine Gefaehrdung als Karte: Faktor aus dem Katalog, Text, Folgen, W/S
 * vorher und nachher mit Matrix-Farbe, Bezug auf Arbeitsmittel und Stoffe
 * aus dem Kataster, Vorschriften, darunter die Massnahmen (S/T/O/P, PSA bei
 * P) und die Vorsorgeanlaesse mit Messwert und Art-Vorschlag.
 *
 * GESPEICHERT WIRD AUTOMATISCH je Gefaehrdung bzw. Zeile (useAutoSpeichern):
 * entprellt nach der letzten Eingabe, sofort beim Verlassen des Feldes und
 * beim Zuklappen. Neue Zeilen entstehen beim ersten sinnvollen Inhalt (Text
 * nicht leer); danach wird mit der zurueckgegebenen Id weitergespeichert —
 * nie eine zweite Zeile. Loeschen bzw. „Entfaellt" bleibt ein bewusster
 * Knopf mit Rueckfrage.
 *
 * Zugeklappt bleibt der Koerper GEMOUNTET (nur ausgeblendet). Nach der ersten
 * Freigabe loescht der Dienst nicht mehr, sondern markiert „entfaellt ab
 * Fassung n"; entfallene Massnahmen und Vorsorgeanlaesse stehen eingeklappt
 * unter der Karte und lassen sich im Entwurf wiederherstellen.
 */
export const GefaehrdungKarte: React.FC<Props> = ({
  gbu, gefaehrdung, vorlage, nr, massnahmen: alleMassnahmen, vorsorge: alleVorsorge, kataster, hinweise, bearbeitbar, companyName, offen, onUmschalten,
  onGespeichert, onEntfernt, onMassnahme, onMassnahmeEntfernt, onVorsorge, onVorsorgeEntfernt, onFehler, onNeuLaden,
}) => {
  /** Rueckfrage offen: Verlassen des Feldes speichert dann nicht (Verwerfen soll nichts mehr anlegen). */
  const frageRef = useRef(false);
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [stelltHer, setStelltHer] = useState<string | null>(null);
  /** Zuklappen laeuft (wartet auf das Speichern) — ein zweiter Klick darf nicht doppelt umschalten. */
  const [klappt, setKlappt] = useState(false);
  const klapptRef = useRef(false);
  const [klappHinweis, setKlappHinweis] = useState(false);
  /** Neue, noch nicht angelegte Zeilen (Schluessel lokal) und die Schluessel bereits angelegter. */
  const [neueMassnahmen, setNeueMassnahmen] = useState<Array<{ key: string; art: MassnahmeArt }>>([]);
  const [neueVorsorge, setNeueVorsorge] = useState<Array<{ key: string; code: string }>>([]);
  const [mSchluessel, setMSchluessel] = useState<Record<string, string>>({});
  const [vSchluessel, setVSchluessel] = useState<Record<string, string>>({});
  const [vorsorgeCode, setVorsorgeCode] = useState('');
  const gruppe = useSpeicherGruppe();

  // Entwurf, eigene Antwort vs. fremder Stand, Kennung fuer das Anlegen, Verwerfen: useZeilenEntwurf.
  const { e, setE, auto, gruende, verworfen, verwerfen: verwerfeZeile, loeschen: loescheZeile } = useZeilenEntwurf({
    zeile: gefaehrdung,
    vorlage,
    regeln: GEFAEHRDUNG_REGELN,
    bearbeitbar,
    schreibe: (x, id, opts) => speichereGefaehrdung(gbu, x, id, companyName, opts),
    loesche: (id) => loescheGefaehrdung(gbu, id),
    onGespeichert,
  });

  const setze = <K extends keyof GefaehrdungEntwurf>(k: K, v: GefaehrdungEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));
  const faktorEintrag = GEFAEHRDUNGSFAKTOREN.find((f) => f.code === e.faktor);
  const rNachHoch = e.wNach * e.sNach >= 6;
  const loesch = loeschText(gbu);
  const angelegt = gefaehrdung;

  const massnahmen = alleMassnahmen.filter((m) => istAktiv(m));
  const vorsorge = alleVorsorge.filter((v) => istAktiv(v));
  const entfalleneMassnahmen = alleMassnahmen.filter((m) => !istAktiv(m));
  const entfalleneVorsorge = alleVorsorge.filter((v) => !istAktiv(v));
  const mListe = mitStabilenSchluesseln(massnahmen, neueMassnahmen, mSchluessel);
  const vListe = mitStabilenSchluesseln(vorsorge, neueVorsorge, vSchluessel);

  /**
   * Zuklappen wartet auf ausstehende Speicherungen der Karte und ihrer Zeilen.
   * Bleibt etwas offen (Fehler, unvollstaendig), bleibt die Karte beim ersten
   * Klick offen und sagt warum; ein zweiter Klick klappt trotzdem zu.
   */
  const umschalten = async () => {
    if (klapptRef.current) return;
    // Eine neue Karte bleibt offen (der Editor fuehrt sie aufgeklappt) — der Klick speichert nur.
    if (!angelegt) { void auto.flush(); return; }
    if (!offen) { setKlappHinweis(false); onUmschalten(); return; }
    klapptRef.current = true;
    setKlappt(true);
    try {
      const ok = (await Promise.all([auto.flush(), gruppe.flush()])).every(Boolean);
      if (zuklappenErlaubt(ok, klappHinweis)) { setKlappHinweis(false); onUmschalten(); } else setKlappHinweis(true);
    } finally {
      klapptRef.current = false;
      setKlappt(false);
    }
  };

  const frageOeffnen = () => { frageRef.current = true; setLoeschFrage(true); };
  const frageSchliessen = () => { frageRef.current = false; setLoeschFrage(false); };

  /** Gespeicherte Gefaehrdung loeschen bzw. (nach einer Freigabe) entfallen lassen. */
  const loeschen = async (z: SifaGbuGefaehrdung) => {
    setLoescht(true);
    try {
      const erg = await loescheZeile(z);
      frageSchliessen();
      // Nach einer Freigabe bleibt die Zeile („entfaellt ab Fassung n") — dann frisch laden.
      if (erg === 'entfallen') onNeuLaden(); else onEntfernt(z.id);
    } catch (err) {
      frageSchliessen();
      onFehler(rechtFehlerOder(err));
    } finally {
      setLoescht(false);
    }
  };

  /**
   * Neue Karte verwerfen. Lief schon ein Anlegen (Entprellung, Verlassen des
   * Feldes), wird dessen Antwort abgewartet und die Zeile wieder geloescht —
   * sonst landete eine verworfene Gefaehrdung in der Fassung.
   */
  const verwerfen = async () => {
    setLoescht(true);
    try {
      const erg = await verwerfeZeile();
      frageSchliessen();
      if (erg.art === 'steht') {
        onFehler(`Verwerfen ging nicht: ${rechtFehlerOder(erg.fehler)} Die Gefährdung ist gespeichert — bitte löschen.`);
        return;
      }
      if (erg.art === 'unklar') onNeuLaden();
      onEntfernt(null);
    } finally {
      setLoescht(false);
    }
  };

  const wiederherstellen = async (art: 'massnahme' | 'vorsorge', id: string) => {
    if (!bearbeitbar || stelltHer) return;
    setStelltHer(id);
    try {
      await stelleWiederHer(art, gbu, id);
      onNeuLaden();
    } catch (err) {
      onFehler(rechtFehlerOder(err));
    } finally {
      setStelltHer(null);
    }
  };

  const arbeitsmittel = bezugsAuswahl(kataster.arbeitsmittel, e.arbeitsmittelIds);
  const stoffe = bezugsAuswahl(kataster.stoffe, e.stoffIds);
  const bezugText = [
    ...e.arbeitsmittelIds.map((id) => kataster.arbeitsmittel.find((a) => a.id === id)?.bezeichnung).filter(Boolean),
    ...e.stoffIds.map((id) => kataster.stoffe.find((s) => s.id === id)?.bezeichnung).filter(Boolean),
  ].join(', ');

  const anzahlSperren = hinweise.filter((h) => h.stufe === 'sperre').length;
  const anzahlWarnungen = hinweise.filter((h) => h.stufe === 'warnung').length;

  return (
    <div id={angelegt ? gefaehrdungAnker(angelegt.id) : undefined} className={`bg-white border rounded-2xl scroll-mt-4 ${anzahlSperren ? 'border-red-300' : rNachHoch ? 'border-red-200' : 'border-slate-200'}`}>
      {/* Kopfzeile: immer sichtbar, klappt auf und zu (Zuklappen speichert vorher). */}
      <button type="button" onClick={() => void umschalten()} aria-busy={klappt} className="w-full text-left p-3 flex items-start gap-2">
        {klappt
          ? <Loader2 className="w-4 h-4 text-slate-400 shrink-0 mt-1 animate-spin" />
          : offen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 mt-1" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />}
        <span className="text-xs font-black text-slate-400 mt-1 w-6 shrink-0">{nr}.</span>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase text-slate-500 truncate">{faktorLabel(e.faktor)}</p>
          <p className="text-sm font-semibold text-slate-800 break-words">{e.gefaehrdung.trim() || <span className="text-slate-400 italic">Gefährdung beschreiben …</span>}</p>
          {!offen && (
            <p className="text-[11px] text-slate-500 mt-0.5">
              {massnahmen.length} Maßnahme(n){vorsorge.length ? ` · ${vorsorge.length} Vorsorge` : ''}{bezugText ? ` · ${bezugText}` : ''}
              {e.quelle === 'ki' ? ' · KI' : e.quelle === 'ersatz' ? ' · Katalog' : ''}
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <RisikoBadge w={e.wVor} s={e.sVor} titel="vorher" />
          <span className="text-slate-400 text-xs">→</span>
          <RisikoBadge w={e.wNach} s={e.sNach} titel="nachher" />
          {(anzahlSperren > 0 || anzahlWarnungen > 0) && <AlertTriangle className={`w-4 h-4 ml-1 ${anzahlSperren ? 'text-red-600' : 'text-amber-500'}`} />}
        </div>
      </button>
      {/* Zugeklappt: der Stand der Karte UND ihrer Zeilen bleibt im Kopf sichtbar. */}
      {bearbeitbar && !offen && (auto.anzeige !== 'ruhig' || gruppe.offen > 0) && (
        <div className="px-3 -mt-1 pb-2 pl-14 flex flex-wrap items-center gap-x-3 gap-y-1">
          <SpeicherStatus anzeige={auto.anzeige} fehler={auto.fehler} gruende={gruende} onErneut={auto.erneut} onNeuLaden={onNeuLaden} />
          {gruppe.offen > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
              <AlertTriangle className="w-3 h-3 shrink-0" /> {offeneZeilenText(gruppe.offen)}
            </span>
          )}
        </div>
      )}
      {bearbeitbar && offen && klappHinweis && (auto.anzeige === 'fehler' || auto.anzeige === 'unvollstaendig' || gruppe.offen > 0) && (
        <p className="mx-3 mb-2 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">{KLAPP_HINWEIS_TEXT}</p>
      )}

      {/* Zugeklappt nur ausgeblendet, nie abgebaut — ungespeicherte Eingaben bleiben.
          Verlassen eines Feldes speichert sofort (onBlur bubbelt in React) — nicht bei offener Rueckfrage. */}
      <SpeicherKontext.Provider value={gruppe.wert}>
        <div className={offen ? 'px-3 pb-3 space-y-4 border-t border-slate-100 pt-3' : 'hidden'} onBlur={() => { if (!frageRef.current) void auto.flush(); }}>
          {hinweise.length > 0 && (
            <ul className="space-y-1">
              {hinweise.map((h, i) => (
                <li key={`${h.code}-${i}`} className={`px-2.5 py-1.5 text-xs border rounded-lg ${HINWEIS_TON[h.stufe]}`}>{h.text}</li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={LABEL}>Gefährdungsfaktor</label>
              <select value={e.faktor} onChange={(ev) => setze('faktor', ev.target.value as GefaehrdungsFaktor)} disabled={!bearbeitbar} className={FELD}>
                {GEFAEHRDUNGSFAKTOREN.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
              </select>
              {bearbeitbar && faktorEintrag && faktorEintrag.beispiele.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-1">z. B. {faktorEintrag.beispiele.slice(0, 3).join(' · ')}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className={LABEL}>Gefährdung</label>
              <textarea value={e.gefaehrdung} onChange={(ev) => setze('gefaehrdung', ev.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} placeholder="z. B. Absturz von der Schalung beim Ein- und Ausschalen" autoFocus={!gefaehrdung && bearbeitbar} />
            </div>
            <div className="md:col-span-2">
              <label className={LABEL}>Mögliche Folgen</label>
              <input value={e.folgen ?? ''} onChange={(ev) => setze('folgen', ev.target.value)} disabled={!bearbeitbar} className={FELD} placeholder="z. B. Knochenbrüche, Kopfverletzungen" />
            </div>
            <RisikoWahl titel="Risiko vorher (ohne Maßnahmen)" w={e.wVor} s={e.sVor} disabled={!bearbeitbar} onChange={(w, s) => setE((p) => ({ ...p, wVor: w, sVor: s }))} />
            <RisikoWahl titel="Risiko nachher (mit Maßnahmen)" w={e.wNach} s={e.sNach} disabled={!bearbeitbar} onChange={(w, s) => setE((p) => ({ ...p, wNach: w, sNach: s }))} />
            {rNachHoch && (
              <p className="md:col-span-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Restrisiko hoch (R nachher ≥ 6): weitere Maßnahmen prüfen. Die Freigabe ist trotzdem möglich, die Zeile wird hervorgehoben.
              </p>
            )}
          </div>

          {/* Bezug ins Kataster: daraus entstehen BA-Bedarf und Gefahrstoffverzeichnis. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Arbeitsmittel</label>
              {arbeitsmittel.length === 0 ? (
                <p className="text-xs text-slate-400">Keine Arbeitsmittel im Betrieb erfasst.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {arbeitsmittel.map((a) => {
                    const an = e.arbeitsmittelIds.includes(a.id);
                    return (
                      <button key={a.id} type="button" disabled={!bearbeitbar} onClick={() => setze('arbeitsmittelIds', schalteId(e.arbeitsmittelIds, a.id))}
                        className={`min-h-[32px] px-2.5 text-xs font-semibold rounded-full border transition-colors ${an ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'} ${!a.aktiv ? 'opacity-60' : ''} disabled:cursor-default`}>
                        {a.bezeichnung}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className={LABEL}>Gefahr- und Biostoffe</label>
              {stoffe.length === 0 ? (
                <p className="text-xs text-slate-400">Keine Stoffe im Betrieb erfasst.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {stoffe.map((s) => {
                    const an = e.stoffIds.includes(s.id);
                    return (
                      <button key={s.id} type="button" disabled={!bearbeitbar} onClick={() => setze('stoffIds', schalteId(e.stoffIds, s.id))}
                        className={`min-h-[32px] px-2.5 text-xs font-semibold rounded-full border transition-colors ${an ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'} ${!s.aktiv ? 'opacity-60' : ''} disabled:cursor-default`}>
                        {s.bezeichnung}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={LABEL}>Vorschriften zu dieser Gefährdung</label>
            <ListenEditor werte={e.vorschriften} onChange={(v) => setze('vorschriften', v)} disabled={!bearbeitbar} platzhalter="z. B. DGUV Regel 101-038" leerText="Keine." />
          </div>

          {bearbeitbar && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SpeicherStatus anzeige={auto.anzeige} fehler={auto.fehler} gruende={gruende} onErneut={auto.erneut} onNeuLaden={onNeuLaden} />
              <button
                type="button"
                // Verwerfen: den Fokus im Feld lassen — sonst legte das Verlassen des Feldes die Zeile erst noch an.
                onMouseDown={angelegt ? undefined : (ev) => ev.preventDefault()}
                onClick={frageOeffnen}
                disabled={verworfen}
                className={`${KNOPF} text-red-700 bg-red-50 hover:bg-red-100 ml-auto`}
              >
                <Trash2 className="w-3.5 h-3.5" /> {angelegt ? (gbu.freigegebeneFassung == null ? 'Gefährdung löschen' : loesch.label) : 'Verwerfen'}
              </button>
            </div>
          )}

          {/* ── Massnahmen ─────────────────────────────────────────────── */}
          {angelegt && (
            <div className="space-y-2">
              <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Maßnahmen (STOP)</p>
              {mListe.length === 0 && <p className="text-xs text-slate-400 italic">Noch keine Maßnahme.</p>}
              {mListe.map((eintrag, i) => (
                <MassnahmeZeile
                  key={eintrag.key}
                  gbu={gbu}
                  gefaehrdungId={angelegt.id}
                  massnahme={eintrag.zeile}
                  vorlage={eintrag.neu ? neueMassnahme(naechsteSortOrder(alleMassnahmen) + i, eintrag.neu.art) : undefined}
                  bearbeitbar={bearbeitbar}
                  companyName={companyName}
                  onGespeichert={(m) => {
                    if (eintrag.neu) {
                      const k = eintrag.neu.key;
                      setMSchluessel((s) => ({ ...s, [m.id]: k }));
                      setNeueMassnahmen((l) => l.filter((x) => x.key !== k));
                    }
                    onMassnahme(m);
                  }}
                  onEntfernt={(erg) => {
                    if (eintrag.neu && erg === 'verworfen') { const k = eintrag.neu.key; setNeueMassnahmen((l) => l.filter((x) => x.key !== k)); return; }
                    if (erg === 'entfallen') onNeuLaden(); else if (eintrag.zeile) onMassnahmeEntfernt(eintrag.zeile.id);
                  }}
                  onFehler={onFehler}
                  onNeuLaden={onNeuLaden}
                />
              ))}
              {bearbeitbar && (
                <div className="flex flex-wrap gap-1.5">
                  {MASSNAHME_ARTEN.map((a) => (
                    <button key={a.art} type="button" onClick={() => setNeueMassnahmen((l) => [...l, { key: crypto.randomUUID(), art: a.art }])}
                      className={`${KNOPF} min-h-[36px] ${MASSNAHME_ART_TON[a.art]} hover:opacity-80`}>
                      <Plus className="w-3.5 h-3.5" /> {a.kurz} – {a.label}
                    </button>
                  ))}
                </div>
              )}
              {entfalleneMassnahmen.length > 0 && (
                <details className="border border-dashed border-slate-300 rounded-xl px-3 py-2">
                  <summary className="text-[11px] font-bold text-slate-500 cursor-pointer">Entfallene Maßnahmen ({entfalleneMassnahmen.length})</summary>
                  <ul className="mt-2 space-y-1.5">
                    {entfalleneMassnahmen.map((m) => (
                      <li key={m.id} className="flex flex-wrap items-start gap-2 text-xs text-slate-500">
                        <span className={`shrink-0 px-1.5 font-black rounded ${MASSNAHME_ART_TON[m.art]} opacity-60`}>{m.art}</span>
                        <span className="flex-1 min-w-0 line-through">{m.text}</span>
                        <span className="text-[11px]">{entfaelltText(m)}</span>
                        {bearbeitbar && (
                          <button type="button" onClick={() => void wiederherstellen('massnahme', m.id)} disabled={!!stelltHer} className="inline-flex items-center gap-1 font-bold text-amber-800 hover:underline disabled:opacity-40">
                            {stelltHer === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Wiederherstellen
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* ── Vorsorge ───────────────────────────────────────────────── */}
          {angelegt && (
            <div className="space-y-2">
              <p className="text-[11px] font-black uppercase tracking-wide text-slate-500 flex items-center gap-1"><Stethoscope className="w-3.5 h-3.5" /> Arbeitsmedizinische Vorsorge</p>
              {vListe.length === 0 && <p className="text-xs text-slate-400 italic">Kein Vorsorgeanlass.</p>}
              {vListe.map((eintrag) => (
                <VorsorgeZeile
                  key={eintrag.key}
                  gbu={gbu}
                  gefaehrdungId={angelegt.id}
                  vorsorge={eintrag.zeile}
                  vorlage={eintrag.neu ? neueVorsorgeFuer(eintrag.neu.code) : undefined}
                  bearbeitbar={bearbeitbar}
                  companyName={companyName}
                  onGespeichert={(v) => {
                    if (eintrag.neu) {
                      const k = eintrag.neu.key;
                      setVSchluessel((s) => ({ ...s, [v.id]: k }));
                      setNeueVorsorge((l) => l.filter((x) => x.key !== k));
                    }
                    onVorsorge(v);
                  }}
                  onEntfernt={(erg) => {
                    if (eintrag.neu && erg === 'verworfen') { const k = eintrag.neu.key; setNeueVorsorge((l) => l.filter((x) => x.key !== k)); return; }
                    if (erg === 'entfallen') onNeuLaden(); else if (eintrag.zeile) onVorsorgeEntfernt(eintrag.zeile.id);
                  }}
                  onFehler={onFehler}
                  onNeuLaden={onNeuLaden}
                />
              ))}
              {bearbeitbar && (
                <div className="flex gap-2">
                  <select value={vorsorgeCode} onChange={(ev) => setVorsorgeCode(ev.target.value)} className={`${FELD} flex-1 min-w-0`}>
                    <option value="">Vorsorgeanlass wählen …</option>
                    {ARBMEDVV_ANLAESSE.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
                  </select>
                  <button type="button" disabled={!vorsorgeCode} onClick={() => { setNeueVorsorge((l) => [...l, { key: crypto.randomUUID(), code: vorsorgeCode }]); setVorsorgeCode(''); }}
                    className={`${KNOPF} text-teal-800 bg-teal-50 hover:bg-teal-100`}>
                    <Plus className="w-3.5 h-3.5" /> Hinzufügen
                  </button>
                </div>
              )}
              {entfalleneVorsorge.length > 0 && (
                <details className="border border-dashed border-slate-300 rounded-xl px-3 py-2">
                  <summary className="text-[11px] font-bold text-slate-500 cursor-pointer">Entfallene Vorsorgeanlässe ({entfalleneVorsorge.length})</summary>
                  <ul className="mt-2 space-y-1.5">
                    {entfalleneVorsorge.map((v) => (
                      <li key={v.id} className="flex flex-wrap items-start gap-2 text-xs text-slate-500">
                        <span className="flex-1 min-w-0 line-through">{anlassLabel(v.anlassCode)}</span>
                        <span className="text-[11px]">{entfaelltText(v)}</span>
                        {bearbeitbar && (
                          <button type="button" onClick={() => void wiederherstellen('vorsorge', v.id)} disabled={!!stelltHer} className="inline-flex items-center gap-1 font-bold text-amber-800 hover:underline disabled:opacity-40">
                            {stelltHer === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Wiederherstellen
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {!angelegt && bearbeitbar && <p className="text-[11px] text-slate-500">Wird angelegt, sobald die Gefährdung beschrieben ist; dann lassen sich Maßnahmen und Vorsorge ergänzen.</p>}
        </div>
      </SpeicherKontext.Provider>

      {loeschFrage && (
        <RueckfrageDialog
          titel={angelegt ? (gbu.freigegebeneFassung == null ? `Gefährdung ${nr} löschen` : `Gefährdung ${nr}: ${loesch.label}`) : 'Neue Gefährdung verwerfen'}
          text={angelegt
            ? (loesch.erklaerung
              ? `„${e.gefaehrdung.trim() || 'ohne Text'}" samt Maßnahmen und Vorsorge aus dieser Fassung nehmen?\n\n${loesch.erklaerung}`
              : `„${e.gefaehrdung.trim() || 'ohne Text'}" samt ${massnahmen.length} Maßnahme(n) und ${vorsorge.length} Vorsorgeanlass/-anlässen löschen?\n\nDas lässt sich nicht rückgängig machen.`)
            : 'Die Eingaben dieser Karte verwerfen?'}
          jaText={angelegt ? (gbu.freigegebeneFassung == null ? 'Löschen' : 'Entfallen lassen') : 'Verwerfen'}
          gefaehrlich
          laeuft={loescht}
          onNein={() => { frageSchliessen(); void auto.flush(); }}
          onJa={() => void (angelegt ? loeschen(angelegt) : verwerfen())}
        />
      )}
    </div>
  );
};

/** Vorgabe eines neuen Vorsorgeanlasses: Einheit aus der Katalogregel, Art aus Vorschlag bzw. Standardart. */
const neueVorsorgeFuer = (code: string): VorsorgeEntwurf => {
  const eintrag = ARBMEDVV_ANLAESSE.find((a) => a.code === code);
  return neueVorsorge({
    anlassCode: code,
    einheit: eintrag?.regel?.einheit ?? null,
    standardArt: eintrag?.standardArt ?? 'pflicht',
    artVorschlag: vorsorgeArtVorschlag(code, null),
  });
};

/** Die reinen Regeln je Zeilenart fuer useZeilenEntwurf (gbuModulLogic) — fest, damit sie sich zwischen den Zeichnungen nicht aendern. */
const GEFAEHRDUNG_REGELN: ZeilenRegeln<SifaGbuGefaehrdung, GefaehrdungEntwurf> = {
  entwurfAus: gefaehrdungEntwurfAus,
  geaendert: gefaehrdungGeaendert,
  begonnen: gefaehrdungBegonnen,
  sinnvoll: gefaehrdungSinnvoll,
  bereinige: bereinigeGefaehrdung,
  pruefe: pruefeGefaehrdung,
};

const MASSNAHME_REGELN: ZeilenRegeln<SifaGbuMassnahme, MassnahmeEntwurf> = {
  entwurfAus: massnahmeEntwurfAus,
  geaendert: massnahmeGeaendert,
  begonnen: massnahmeBegonnen,
  sinnvoll: massnahmeSinnvoll,
  bereinige: bereinigeMassnahme,
  pruefe: pruefeMassnahme,
};

const VORSORGE_REGELN: ZeilenRegeln<SifaGbuVorsorge, VorsorgeEntwurf> = {
  entwurfAus: vorsorgeEntwurfAus,
  geaendert: vorsorgeGeaendert,
  begonnen: () => true,
  sinnvoll: (e) => !!(e.anlassCode || '').trim(),
  bereinige: bereinigeVorsorge,
  pruefe: (e) => ((e.anlassCode || '').trim() ? [] : ['Bitte einen Vorsorgeanlass wählen.']),
};

/** Ergebnis eines Loeschens: wirklich geloescht, (nach einer Freigabe) „entfaellt ab Fassung n", oder eine nie angelegte Zeile verworfen. */
type LoeschErgebnis = Awaited<ReturnType<typeof loescheMassnahme>> | 'verworfen';

// ─── Massnahme ──────────────────────────────────────────────────────────────

const MassnahmeZeile: React.FC<{
  gbu: SifaGbu;
  gefaehrdungId: string;
  massnahme: SifaGbuMassnahme | null;
  vorlage?: MassnahmeEntwurf;
  bearbeitbar: boolean;
  companyName: string | null;
  onGespeichert: (m: SifaGbuMassnahme) => void;
  onEntfernt: (erg: LoeschErgebnis) => void;
  onFehler: (text: string) => void;
  onNeuLaden: () => void;
}> = ({ gbu, gefaehrdungId, massnahme, vorlage, bearbeitbar, companyName, onGespeichert, onEntfernt, onFehler, onNeuLaden }) => {
  const [loeschFrage, setLoeschFrage] = useState(false);
  const { e, setE, auto, gruende, verworfen, verwerfen: verwerfeZeile, loeschen: loescheZeile } = useZeilenEntwurf({
    zeile: massnahme,
    vorlage,
    regeln: MASSNAHME_REGELN,
    bearbeitbar,
    schreibe: (x, id, opts) => speichereMassnahme(gbu, gefaehrdungId, x, id, companyName, opts),
    loesche: (id) => loescheMassnahme(gbu, id),
    onGespeichert,
  });
  const setze = <K extends keyof MassnahmeEntwurf>(k: K, v: MassnahmeEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));
  const loesch = loeschText(gbu);
  const angelegt = massnahme;

  const loeschen = async () => {
    setLoeschFrage(false);
    if (!angelegt) return;
    try {
      onEntfernt(await loescheZeile(angelegt));
    } catch (err) {
      onFehler(rechtFehlerOder(err));
    }
  };

  /** Neue Zeile verwerfen — ein laufendes Anlegen wird abgewartet und wieder geloescht. */
  const verwerfen = async () => {
    const erg = await verwerfeZeile();
    if (erg.art === 'steht') { onFehler(`Verwerfen ging nicht: ${rechtFehlerOder(erg.fehler)} Die Maßnahme ist gespeichert — bitte löschen.`); return; }
    if (erg.art === 'unklar') onNeuLaden();
    onEntfernt('verworfen');
  };

  const stand = angelegt?.wirksamGeprueftAm
    ? `${angelegt.wirksam === false ? 'unwirksam' : 'wirksam'} (${formatTag(angelegt.wirksamGeprueftAm)})`
    : angelegt?.umgesetztGemeldetAm ? `umgesetzt gemeldet ${formatTag(angelegt.umgesetztGemeldetAm)}` : null;

  return (
    <div className="border border-slate-200 rounded-xl p-2.5 space-y-2 bg-slate-50/40" onBlur={() => { void auto.flush(); }}>
      <div className="grid grid-cols-[auto_1fr] gap-2 items-start">
        <select value={e.art} onChange={(ev) => setze('art', ev.target.value as MassnahmeArt)} disabled={!bearbeitbar}
          className={`min-h-[40px] px-2 text-sm font-black rounded-lg outline-none ${MASSNAHME_ART_TON[e.art]} disabled:opacity-90`} title="Art der Maßnahme (STOP)">
          {MASSNAHME_ARTEN.map((a) => <option key={a.art} value={a.art}>{a.kurz}</option>)}
        </select>
        <textarea value={e.text} onChange={(ev) => setze('text', ev.target.value)} disabled={!bearbeitbar} rows={1} className={`${FELD} resize-y`} placeholder="Maßnahme beschreiben" autoFocus={!massnahme && bearbeitbar} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {e.art === 'P' && (
          <label className="block">
            <span className={LABEL}>PSA</span>
            <select value={e.psaCode ?? ''} onChange={(ev) => setze('psaCode', ev.target.value || null)} disabled={!bearbeitbar} className={FELD}>
              <option value="">— keine Auswahl —</option>
              {PSA_KATALOG.map((p) => <option key={p.code} value={p.code}>{p.label}{p.norm ? ` (${p.norm})` : ''}</option>)}
              {e.psaCode && !PSA_KATALOG.some((p) => p.code === e.psaCode) && <option value={e.psaCode}>{psaLabel(e.psaCode)}</option>}
            </select>
          </label>
        )}
        <label className="block">
          <span className={LABEL}>Verweis</span>
          <select value={e.verweis ?? ''} onChange={(ev) => setze('verweis', (ev.target.value || null) as MassnahmeVerweis | null)} disabled={!bearbeitbar} className={FELD}>
            <option value="">—</option>
            {VERWEISE.map((v) => <option key={v} value={v}>{VERWEIS_LABEL[v]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Verantwortlich</span>
          <input value={e.verantwortlich ?? ''} onChange={(ev) => setze('verantwortlich', ev.target.value)} disabled={!bearbeitbar} className={FELD} placeholder="z. B. Polier" />
        </label>
        <label className="block">
          <span className={LABEL}>Termin (leer = laufend)</span>
          <input type="date" value={e.termin ?? ''} onChange={(ev) => setze('termin', ev.target.value || null)} disabled={!bearbeitbar} className={FELD} />
        </label>
      </div>
      {stand && <p className="text-[11px] text-slate-500">Stand: {stand}</p>}
      {bearbeitbar && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SpeicherStatus anzeige={auto.anzeige} fehler={auto.fehler} gruende={gruende} onErneut={auto.erneut} onNeuLaden={onNeuLaden} />
          <button
            type="button"
            // Verwerfen: Fokus im Feld lassen — sonst legte das Verlassen des Feldes die Zeile erst noch an.
            onMouseDown={angelegt ? undefined : (ev) => ev.preventDefault()}
            onClick={() => (angelegt ? setLoeschFrage(true) : void verwerfen())}
            disabled={verworfen}
            className={`${KNOPF} min-h-[36px] text-red-700 hover:bg-red-50 ml-auto`}
          >
            <Trash2 className="w-3.5 h-3.5" /> {angelegt ? loesch.label : 'Verwerfen'}
          </button>
        </div>
      )}
      {loeschFrage && (
        <RueckfrageDialog
          titel={`Maßnahme: ${loesch.label}`}
          text={loesch.erklaerung ? `„${e.text.trim() || 'ohne Text'}" aus dieser Fassung nehmen?\n\n${loesch.erklaerung}` : `„${e.text.trim() || 'ohne Text'}" löschen?`}
          jaText={loesch.erklaerung ? 'Entfallen lassen' : 'Löschen'}
          gefaehrlich
          onNein={() => setLoeschFrage(false)}
          onJa={() => void loeschen()}
        />
      )}
    </div>
  );
};

// ─── Vorsorge ───────────────────────────────────────────────────────────────

const VorsorgeZeile: React.FC<{
  gbu: SifaGbu;
  gefaehrdungId: string;
  vorsorge: SifaGbuVorsorge | null;
  vorlage?: VorsorgeEntwurf;
  bearbeitbar: boolean;
  companyName: string | null;
  onGespeichert: (v: SifaGbuVorsorge) => void;
  onEntfernt: (erg: LoeschErgebnis) => void;
  onFehler: (text: string) => void;
  onNeuLaden: () => void;
}> = ({ gbu, gefaehrdungId, vorsorge, vorlage, bearbeitbar, companyName, onGespeichert, onEntfernt, onFehler, onNeuLaden }) => {
  const [messwertText, setMesswertText] = useState(() => zahlText(vorsorge?.messwert ?? vorlage?.messwert ?? null));
  const [loeschFrage, setLoeschFrage] = useState(false);
  // Ein neuer Vorsorgeanlass ist mit der Wahl des Anlasses sinnvoll — er wird gleich angelegt (VORSORGE_REGELN).
  const { e, setE, auto, gruende, verworfen, verwerfen: verwerfeZeile, loeschen: loescheZeile } = useZeilenEntwurf({
    zeile: vorsorge,
    vorlage,
    regeln: VORSORGE_REGELN,
    bearbeitbar,
    schreibe: (x, id, opts) => speichereVorsorge(gbu, gefaehrdungId, x, id, companyName, opts),
    loesche: (id) => loescheVorsorge(gbu, id),
    onGespeichert,
    onUebernommen: (v) => setMesswertText(zahlText(v.messwert)),
    zusatzSchluessel: messwertText,
    zusatzGruende: messwertText.trim() && parseZahl(messwertText) === null ? ['Der Messwert ist keine Zahl.'] : [],
  });
  const eintrag = ARBMEDVV_ANLAESSE.find((a) => a.code === e.anlassCode);
  const standardArt: VorsorgeArt = eintrag?.standardArt ?? 'pflicht';
  const loesch = loeschText(gbu);
  const angelegt = vorsorge;

  /** Anlass oder Messwert geaendert: Vorschlag neu rechnen, die Art folgt, solange sie nicht bewusst gesetzt wurde. */
  const mitVorschlag = (neu: VorsorgeEntwurf): VorsorgeEntwurf => {
    const std = ARBMEDVV_ANLAESSE.find((a) => a.code === neu.anlassCode)?.standardArt ?? 'pflicht';
    return vorsorgeMitVorschlag(neu, vorsorgeArtVorschlag(neu.anlassCode, neu.messwert, neu.einheit), std);
  };

  /** Anlass gewechselt: ohne Regel bzw. mit anderer Einheit faellt der Messwert weg. */
  const anlassWechseln = (code: string) => {
    const neu = mitVorschlag(vorsorgeNachAnlasswechsel(e, code, ARBMEDVV_ANLAESSE.find((a) => a.code === code)?.regel ?? null));
    setE(neu);
    setMesswertText(zahlText(neu.messwert));
  };

  const loeschen = async () => {
    setLoeschFrage(false);
    if (!angelegt) return;
    try {
      onEntfernt(await loescheZeile(angelegt));
    } catch (err) {
      onFehler(rechtFehlerOder(err));
    }
  };

  /** Neuen Anlass verwerfen — ein laufendes Anlegen wird abgewartet und wieder geloescht. */
  const verwerfen = async () => {
    const erg = await verwerfeZeile();
    if (erg.art === 'steht') { onFehler(`Verwerfen ging nicht: ${rechtFehlerOder(erg.fehler)} Der Vorsorgeanlass ist gespeichert — bitte löschen.`); return; }
    if (erg.art === 'unklar') onNeuLaden();
    onEntfernt('verworfen');
  };

  const regel = eintrag?.regel;
  return (
    <div className="border border-teal-200 bg-teal-50/40 rounded-xl p-2.5 space-y-2" onBlur={() => { void auto.flush(); }}>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-2">
        <label className="block">
          <span className={LABEL}>Anlass (ArbMedVV)</span>
          <select value={e.anlassCode} onChange={(ev) => anlassWechseln(ev.target.value)} disabled={!bearbeitbar} className={FELD}>
            {ARBMEDVV_ANLAESSE.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
            {!eintrag && <option value={e.anlassCode}>{anlassLabel(e.anlassCode)}</option>}
          </select>
          {eintrag?.fundstelle && <span className="block text-[10px] text-slate-500 mt-0.5">{eintrag.fundstelle}</span>}
        </label>
        <label className="block">
          <span className={LABEL}>Messwert{e.einheit ? ` (${e.einheit})` : ''}</span>
          <input
            value={messwertText}
            inputMode="decimal"
            disabled={!bearbeitbar || !regel}
            onChange={(ev) => {
              setMesswertText(ev.target.value);
              const n = parseZahl(ev.target.value);
              setE((p) => mitVorschlag({ ...p, messwert: n }));
            }}
            className={FELD}
            placeholder={regel ? `z. B. ${regel.pflichtAb ?? regel.angebotUeber ?? ''}` : 'keine Regel'}
          />
        </label>
        <label className="block">
          <span className={LABEL}>Art</span>
          <select value={e.art} onChange={(ev) => setE((p) => ({ ...p, art: ev.target.value as VorsorgeArt }))} disabled={!bearbeitbar} className={FELD}>
            {VORSORGE_ARTEN.map((a) => <option key={a} value={a}>{VORSORGE_ART_LABEL[a]}</option>)}
          </select>
        </label>
      </div>
      <p className="text-[11px] text-slate-600">
        {e.artVorschlag
          ? <>Vorschlag aus Katalog{e.messwert != null ? ' und Messwert' : ''}: <b>{VORSORGE_ART_LABEL[e.artVorschlag]}</b></>
          : <>Ohne Regel im Katalog — Standard: {VORSORGE_ART_LABEL[standardArt]}</>}
        {regel && (regel.angebotUeber != null || regel.pflichtAb != null) && (
          <> · {regel.angebotUeber != null ? `Angebot über ${regel.angebotUeber}` : ''}{regel.angebotUeber != null && regel.pflichtAb != null ? ', ' : ''}{regel.pflichtAb != null ? `Pflicht ab ${regel.pflichtAb}` : ''} {regel.einheit}</>
        )}
      </p>
      {(vorsorgeAbweichung(e) || e.begruendung) && (
        <label className="block">
          <span className={LABEL}>Begründung{vorsorgeAbweichung(e) ? ' (Abweichung vom Vorschlag)' : ''}</span>
          <input value={e.begruendung ?? ''} onChange={(ev) => setE((p) => ({ ...p, begruendung: ev.target.value }))} disabled={!bearbeitbar} className={FELD} />
        </label>
      )}
      {bearbeitbar && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SpeicherStatus anzeige={auto.anzeige} fehler={auto.fehler} gruende={gruende} onErneut={auto.erneut} onNeuLaden={onNeuLaden} />
          <button
            type="button"
            onMouseDown={angelegt ? undefined : (ev) => ev.preventDefault()}
            onClick={() => (angelegt ? setLoeschFrage(true) : void verwerfen())}
            disabled={verworfen}
            className={`${KNOPF} min-h-[36px] text-red-700 hover:bg-red-50 ml-auto`}
          >
            <Trash2 className="w-3.5 h-3.5" /> {angelegt ? loesch.label : 'Verwerfen'}
          </button>
        </div>
      )}
      {loeschFrage && (
        <RueckfrageDialog
          titel={`Vorsorgeanlass: ${loesch.label}`}
          text={loesch.erklaerung ? `„${anlassLabel(e.anlassCode)}" aus dieser Fassung nehmen?\n\n${loesch.erklaerung}` : `„${anlassLabel(e.anlassCode)}" löschen?`}
          jaText={loesch.erklaerung ? 'Entfallen lassen' : 'Löschen'}
          gefaehrlich
          onNein={() => setLoeschFrage(false)}
          onJa={() => void loeschen()}
        />
      )}
    </div>
  );
};
